// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Equipment Documentation Q&A service.
 *
 * Single request/response call to the BFF equipment-docs endpoint. A thin
 * downstream service fronts the Foundry agent and returns a clean JSON object,
 * so this is a plain POST via the shared api wrapper (bearer token + 401/session
 * handling live there).
 */

import { ENDPOINTS } from '../utils/constants';
import { post } from './api';
import type { Citation, EquipmentDocsRequest, EquipmentDocsResponse } from '../types';

/**
 * Normalize a raw citation into our `Citation` shape.
 *
 * This is the SINGLE frontend place coupled to the citation shape; if the
 * contract ever moves, only this mapper changes.
 */
function toCitation(raw: unknown): Citation | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const sourceDoc = obj.source_doc ?? obj.filename ?? obj.title;
  if (typeof sourceDoc !== 'string' || !sourceDoc) return null;
  const pageVal = obj.page ?? obj.page_number;
  return {
    source_doc: sourceDoc,
    page: typeof pageVal === 'number' ? pageVal : null,
    snippet: typeof obj.snippet === 'string' ? obj.snippet : null,
    url: typeof obj.url === 'string' ? obj.url : null,
  };
}

/**
 * Coerce the raw endpoint payload into a well-formed `EquipmentDocsResponse`.
 */
export function normalizeResponse(raw: EquipmentDocsResponse): EquipmentDocsResponse {
  return {
    answer: typeof raw.answer === 'string' ? raw.answer : '',
    no_answer: Boolean(raw.no_answer),
    citations: Array.isArray(raw.citations)
      ? raw.citations.map(toCitation).filter((c): c is Citation => c !== null)
      : [],
    equipment_type: raw.equipment_type ?? null,
    request_id: raw.request_id ?? null,
  };
}

/**
 * Ask the Equipment Documentation Agent a grounded question.
 */
export async function askEquipmentQuestion(
  request: EquipmentDocsRequest
): Promise<EquipmentDocsResponse> {
  const raw = await post<EquipmentDocsResponse>(ENDPOINTS.EQUIPMENT_DOCS_ASK, request);
  return normalizeResponse(raw);
}
